import { ColorModeContext, useMode } from './global/theme/theme';

import { CssBaseline, ThemeProvider } from '@mui/material';
import { Routes, Route } from 'react-router-dom';
import RequireAuth from './auth/RequireAuth';
import { RequirePermission } from './auth/RequirePermission';
import { AppConfigProvider } from './AppConfig';
import { ExoShowLayout } from './components/exo/ExoShowLayout';
import { UserPreferencesProvider } from './global/useUserPreferences';
import { EmailClientProvider } from './components/email-system/useEmailClient';
import { ChatClientProvider } from './components/chat-system/useChatClient';
import { ChatPopupNotification } from './components/chat-system/ChatPopupNotification';
import { ChatUrgentMessageOverlay } from './components/chat-system/ChatUrgentMessageOverlay';
import StandaloneAuth from './auth/StandaloneAuth';
import { lazyPage } from './global/lazyPage';
import { PageSuspense } from './global/PageSuspense';

// Pages load when first opened, not all at startup: the single bundle was
// over 3 MB (gzipped) that phones had to download, parse and compile first.
const Dashboard = lazyPage(() => import('./scenes/dashboard'));
const Invoices = lazyPage(() => import('./scenes/invoices'));
const Offers = lazyPage(() => import('./scenes/offers'));
const Companies = lazyPage(() => import('./scenes/companies'));
const Contacts = lazyPage(() => import('./scenes/contacts'));
const Requests = lazyPage(() => import('./scenes/requests'));
const NewRequests = lazyPage(() => import('./scenes/new-requests'));
const Calendar = lazyPage(() => import('./scenes/calendar'));
const Help = lazyPage(() => import('./scenes/help'));
const Documentation = lazyPage(() => import('./scenes/documentation'));
const DocumentationLayout = lazyPage(() => import('./scenes/documentation/DocumentationLayout'), 'DocumentationLayout');
const ArticleView = lazyPage(() => import('./scenes/documentation/ArticleView'), 'ArticleView');
const CourseView = lazyPage(() => import('./scenes/documentation/CourseView'), 'CourseView');
const Projects = lazyPage(() => import('./scenes/projects'));
const ServerMails = lazyPage(() => import('./scenes/servermails'));
const Explorer = lazyPage(() => import('./scenes/explorer'));
const Employees = lazyPage(() => import('./scenes/employees'));
const TimeTracking = lazyPage(() => import('./scenes/time-tracking'));
const Login = lazyPage(() => import('./open-scenes/login'));
const Recruting = lazyPage(() => import('./scenes/recruting'));
const Contracts = lazyPage(() => import('./scenes/contracts'));
const Teams = lazyPage(() => import('./scenes/teams'));
const Settings = lazyPage(() => import('./scenes/settings'));
const BankAccounts = lazyPage(() => import('./scenes/bank-accounts'));
const SerialMails = lazyPage(() => import('./scenes/serial-mail'));
const Products = lazyPage(() => import('./scenes/products'));
const Orders = lazyPage(() => import('./scenes/orders'));
const Services = lazyPage(() => import('./scenes/services'));
const DigitalSignaturePortal = lazyPage(() => import('./open-scenes/digital-signature'));
const Categories = lazyPage(() => import('./scenes/categories'));
const EmploymentContractPresets = lazyPage(() => import('./scenes/employee-contract-presets'));
const TimeManager = lazyPage(() => import('./scenes/time-manager'));
const UserTimeDetails = lazyPage(() => import('./scenes/user-time-details'));
const SetPassword = lazyPage(() => import('./open-scenes/set-password'), 'SetPassword');
const ContractPresets = lazyPage(() => import('./scenes/contract-presets'));
const PaymentReminder = lazyPage(() => import('./scenes/payment-reminder'));
const TaxConsultant = lazyPage(() => import('./scenes/tax-consultant'));
const Profile = lazyPage(() => import('./scenes/profile'), 'Profile');
const Install = lazyPage(() => import('./open-scenes/install'));
const EmailApp = lazyPage(() => import('./scenes/email-app'));
const ChatApp = lazyPage(() => import('./scenes/chat-app'));
const GetStarted = lazyPage(() => import('./open-scenes/get-started'), 'GetStarted');
const ShowInvoice = lazyPage(() => import('./scenes/invoices/ShowInvoice'), 'ShowInvoice');
const ShowOffer = lazyPage(() => import('./scenes/offers/ShowOffer'), 'ShowOffer');
const ShowOrder = lazyPage(() => import('./scenes/orders/ShowOrder'), 'ShowOrder');
const ShowProject = lazyPage(() => import('./scenes/projects/ShowProject'), 'ShowProject');
const ShowProduct = lazyPage(() => import('./scenes/products/ShowProduct'), 'ShowProduct');
const ShowCompany = lazyPage(() => import('./scenes/companies/ShowCompany'), 'ShowCompany');
const ShowContact = lazyPage(() => import('./scenes/contacts/ShowContact'), 'ShowContact');
const ShowService = lazyPage(() => import('./scenes/services/ShowService'), 'ShowService');
const ShowEmployee = lazyPage(() => import('./scenes/employees/ShowEmployee'), 'ShowEmployee');
const FinanceDashboard = lazyPage(() => import('./scenes/finance-dashboard'));
const ProjectManagement = lazyPage(() => import('./scenes/project-management/ProjectManagement'), 'ProjectManagement');
const Letters = lazyPage(() => import('./scenes/letter'), 'Letters');
const TermsOfService = lazyPage(() => import('./open-scenes/terms-of-service'));
const DeliveryNotes = lazyPage(() => import('./scenes/delivery-note'));
const ShowDeliveryNote = lazyPage(() => import('./scenes/delivery-note/ShowDeliveryNote'), 'ShowDeliveryNote');
const ProjectManagementWorkPackage = lazyPage(() => import('./scenes/project-management/project-work-package'), 'ProjectManagementWorkPackage');
const Expenses = lazyPage(() => import('./scenes/expenses'));
const PendingFinancials = lazyPage(() => import('./scenes/pending-financials'), 'PendingFinancials');
const ShowLetter = lazyPage(() => import('./scenes/letter/ShowLetter'), 'ShowLetter');
const ShowContract = lazyPage(() => import('./scenes/contracts/ShowContract'), 'ShowContract');
const ShowPaymentReminder = lazyPage(() => import('./scenes/payment-reminder/ShowPaymentReminder'), 'ShowPaymentReminder');
const ShowMail = lazyPage(() => import('./scenes/servermails/ShowMail'), 'ShowMail');
const ShowPayroll = lazyPage(() => import('./scenes/expenses/payrolles/ShowPayroll'), 'ShowPayroll');
const ShowReceipt = lazyPage(() => import('./scenes/expenses/receipts/ShowReceipt'), 'ShowReceipt');
const ShowTimeSheet = lazyPage(() => import('./scenes/tax-consultant/timesheets/ShowTimeSheet'), 'ShowTimeSheet');
const ShowAccounting = lazyPage(() => import('./scenes/tax-consultant/accounting/ShowAccounting'), 'ShowAccounting');
const ShowRequest = lazyPage(() => import('./scenes/requests/ShowRequest'), 'ShowRequest');
const ShowNewRequest = lazyPage(() => import('./scenes/new-requests/ShowNewRequest'), 'ShowNewRequest');
const ShowBankAccount = lazyPage(() => import('./scenes/bank-accounts/ShowBankAccount'));
const Invites = lazyPage(() => import('./open-scenes/invites'), 'Invites');
const Leads = lazyPage(() => import('./scenes/leads'));
const ShowLeadsCompany = lazyPage(() => import('./scenes/leads/show/ShowLeadsCompany'), 'ShowLeadsCompany');
const ShowLeadsPersons = lazyPage(() => import('./scenes/leads/show/ShowLeadsPersons'), 'ShowLeadsPersons');
const SalesObjects = lazyPage(() => import('./scenes/sales-objects'));
const ShowSalesObject = lazyPage(() => import('./scenes/sales-objects/ShowSalesObject'), 'ShowSalesObject');
const SalesObjectCustomerView = lazyPage(() => import('./open-scenes/sales-object-customer-view'), 'SalesObjectCustomerView');
const PrivacyPolicy = lazyPage(() => import('./open-scenes/privacy-policy'));
const CustomerDataForm = lazyPage(() => import('./open-scenes/customer-data-form'));
const PublicBooking = lazyPage(() => import('./open-scenes/public-booking'));
const BookingPages = lazyPage(() => import('./scenes/booking-pages'));
const WebForms = lazyPage(() => import('./scenes/web-forms'));
const RecurringInvoicePresets = lazyPage(() => import('./scenes/recurring-invoice-presets'));
const RecurringInvoices = lazyPage(() => import('./scenes/recurring-invoices'));
const ShowRecurringInvoice = lazyPage(() => import('./scenes/recurring-invoices/ShowRecurringInvoice'), 'ShowRecurringInvoice');
const EMails = lazyPage(() => import('./scenes/emails'));
const Chat = lazyPage(() => import('./scenes/chat'));
const Presets = lazyPage(() => import('./scenes/presets'));
const SalesObjectSellerConfirmation = lazyPage(() => import('./open-scenes/sales-object-seller-confirmation'), 'SalesObjectSellerConfirmation');
const ShowRecurringReceipt = lazyPage(() => import('./scenes/expenses/periodic-expenses/ShowRecurringReceipt'), 'ShowRecurringReceipt');
const ProjectControlling = lazyPage(() => import('./scenes/project-controlling'));
const EmployeeCapacity = lazyPage(() => import('./scenes/employee-capacity'));
const Pooling = lazyPage(() => import('./scenes/pooling'));
const RightOfWidthdrawal = lazyPage(() => import('./open-scenes/right-of-withdrawal'));

function App() {
  const [theme, colorMode] = useMode();

  return (
    <AppConfigProvider>
      <UserPreferencesProvider>
        <EmailClientProvider>
          <ChatClientProvider>
            <ColorModeContext.Provider value={colorMode}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <ChatPopupNotification />
                <ChatUrgentMessageOverlay />
                <PageSuspense>
                <Routes>
                  <Route
                    path="sales-object-seller-confirmation/:key/:status"
                    element={<SalesObjectSellerConfirmation />}
                  />
                  <Route path="invites/:key" element={<Invites />} />
                  <Route
                    path="customer-data-form/:type/:key"
                    element={<CustomerDataForm />}
                  />
                  <Route path="book/:token" element={<PublicBooking />} />
                  <Route path="invites/:key/:status" element={<Invites />} />
                  <Route path="terms-of-service" element={<TermsOfService />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route
                    path="withdrawal-declaration"
                    element={<RightOfWidthdrawal />}
                  />
                  <Route
                    path="sales-object-customer-view/:token"
                    element={<SalesObjectCustomerView />}
                  />
                  <Route
                    path="digital-signature/:token"
                    element={<DigitalSignaturePortal />}
                  />
                  <Route path="get-started/:token" element={<GetStarted />} />
                  <Route
                    path="set-password/:email/:token"
                    element={<SetPassword />}
                  />
                  <Route path="*" element={<Login />} />
                  <Route path="login" element={<Login />} />
                  <Route element={<RequireAuth noLayout />}>
                    <Route path="install" element={<Install />} />
                  </Route>

                  {/* Standalone help center — no default ExoPaper navigation */}
                  <Route element={<RequireAuth noLayout />}>
                    <Route element={<DocumentationLayout />}>
                      <Route
                        path="documentation"
                        element={<Documentation />}
                      />
                      <Route
                        path="documentation/articles/:slug"
                        element={<ArticleView />}
                      />
                      <Route
                        path="documentation/courses/:courseSlug"
                        element={<CourseView />}
                      />
                    </Route>
                  </Route>

                  {/* Standalone Apps with independent authentication */}
                  <Route
                    path="email-app"
                    element={
                      <StandaloneAuth appName="ExoMail">
                        <EmailApp />
                      </StandaloneAuth>
                    }
                  />
                  <Route
                    path="chat-app"
                    element={
                      <StandaloneAuth appName="ExoChat">
                        <ChatApp />
                      </StandaloneAuth>
                    }
                  />

                  <Route element={<RequireAuth />}>
                    <Route index element={<Dashboard />} />
                    <Route path="finance" element={<FinanceDashboard />} />
                    <Route
                      path="pending-financials"
                      element={<PendingFinancials />}
                    />
                    <Route path="sales-objects" element={<SalesObjects />} />
                    <Route
                      path="sales-objects/:id"
                      element={<ShowSalesObject />}
                    />
                    <Route path="employees" element={<Employees />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="contacts" element={<Contacts />} />
                    <Route path="companies" element={<Companies />} />
                    <Route path="projects" element={<Projects />} />
                    <Route path="products" element={<Products />} />
                    <Route path="services" element={<Services />} />
                    <Route path="servermails" element={<ServerMails />} />
                    <Route path="explorer" element={<Explorer />} />
                    <Route path="offers" element={<Offers />} />
                    <Route path="web-forms" element={<WebForms />} />
                    <Route path="leads" element={<Leads />} />
                    <Route
                      path="leads/companies/:id"
                      element={<ShowLeadsCompany />}
                    />
                    <Route
                      path="leads/persons/:id"
                      element={<ShowLeadsPersons />}
                    />
                    <Route path="emails" element={<EMails />} />
                    <Route path="emails/:id" element={<EMails />} />
                    <Route path="chat" element={<Chat />} />
                    <Route path="time-tracking" element={<TimeTracking />} />
                    <Route path="invoices" element={<Invoices />} />
                    <Route path="letters" element={<Letters />} />
                    <Route path="requests" element={<Requests />} />
                    <Route path="new-requests" element={<NewRequests />} />
                    <Route path="help" element={<Help />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="calendar" element={<Calendar />} />
                    <Route path="contracts" element={<Contracts />} />
                    <Route
                      path="recurring-invoices"
                      element={<RecurringInvoices />}
                    />
                    <Route path="booking-pages" element={<BookingPages />} />
                    <Route
                      path="project-controlling"
                      element={<ProjectControlling />}
                    />
                    <Route
                      path="employee-capacity"
                      element={<EmployeeCapacity />}
                    />
                    <Route path="pool" element={<Pooling />} />

                    <Route path="recruting" element={<Recruting />} />
                    <Route path="teams" element={<Teams />} />
                    <Route path="expenses" element={<Expenses />} />
                    <Route path="bank-accounts" element={<BankAccounts />} />
                    <Route path="serial-mails" element={<SerialMails />} />
                    <Route path="tax-consultant" element={<TaxConsultant />} />
                    <Route path="time-manager" element={<TimeManager />} />
                    <Route
                      path="time-manager/:userId"
                      element={<UserTimeDetails />}
                    />
                    <Route path="orders" element={<Orders />} />
                    <Route path="delivery-notes" element={<DeliveryNotes />} />
                    <Route
                      path="work-contract-presets"
                      element={<EmploymentContractPresets />}
                    />
                    <Route
                      path="contract-presets"
                      element={<ContractPresets />}
                    />
                    <Route
                      path="recurring-invoice-presets"
                      element={<RecurringInvoicePresets />}
                    />
                    <Route path="presets/:type" element={<Presets />} />

                    <Route path="orders/new/:offerId" element={<Orders />} />
                    <Route path="categories/:type" element={<Categories />} />
                    <Route
                      path="payment-reminder"
                      element={<PaymentReminder />}
                    />
                    <Route element={<ExoShowLayout />}>
                      <Route
                        element={
                          <RequirePermission permission="project" level="read" />
                        }
                      >
                        <Route
                          path="projects/management/:pid"
                          element={<ProjectManagement />}
                        />
                        <Route
                          path="projects/management/:pid/work-package/:wid"
                          element={<ProjectManagementWorkPackage />}
                        />
                      </Route>
                      <Route path="offers/:id" element={<ShowOffer />} />
                      <Route path="invoices/:id" element={<ShowInvoice />} />
                      <Route path="orders/:id" element={<ShowOrder />} />
                      <Route
                        element={
                          <RequirePermission permission="project" level="read" />
                        }
                      >
                        <Route
                          path="projects/:id"
                          element={<ShowProject />}
                        />
                      </Route>
                      <Route path="products/:id" element={<ShowProduct />} />
                      <Route path="services/:id" element={<ShowService />} />
                      <Route path="companies/:id" element={<ShowCompany />} />
                      <Route path="contacts/:id" element={<ShowContact />} />
                      <Route
                        path="recurring-invoices/:id"
                        element={<ShowRecurringInvoice />}
                      />
                      <Route path="employees/:id" element={<ShowEmployee />} />
                      <Route
                        path="delivery-notes/:id"
                        element={<ShowDeliveryNote />}
                      />
                      <Route path="letters/:id" element={<ShowLetter />} />
                      <Route path="contracts/:id" element={<ShowContract />} />
                      <Route
                        path="payment-reminders/:id"
                        element={<ShowPaymentReminder />}
                      />
                      <Route path="servermails/:id" element={<ShowMail />} />
                      <Route
                        path="expenses/payrolls/:id"
                        element={<ShowPayroll />}
                      />
                      <Route
                        path="expenses/receipts/:id"
                        element={<ShowReceipt />}
                      />
                      <Route
                        path="expenses/recurring-receipts/:id"
                        element={<ShowRecurringReceipt />}
                      />
                      <Route
                        path="tax-consultant/timesheets/:id"
                        element={<ShowTimeSheet />}
                      />
                      <Route
                        path="tax-consultant/accounting-exports/:id"
                        element={<ShowAccounting />}
                      />
                    </Route>
                    <Route path="requests/:id" element={<ShowRequest />} />
                    <Route
                      path="new-requests/:id"
                      element={<ShowNewRequest />}
                    />
                    <Route
                      path="bank-accounts/:id"
                      element={<ShowBankAccount />}
                    />
                  </Route>
                </Routes>
                </PageSuspense>
              </ThemeProvider>
            </ColorModeContext.Provider>
          </ChatClientProvider>
        </EmailClientProvider>
      </UserPreferencesProvider>
    </AppConfigProvider>
  );
}

export default App;
